import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useThemeMode } from "../../ThemeContext";
import { APP_URLS } from "../../helpers/url_helper";
import { dataAPI } from "../../helpers/api_service";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const FIELD_TYPES = [
  { value: "text", label: "Short Answer" },
  { value: "textarea", label: "Paragraph" },
  { value: "radio", label: "Option Button (Single Choice)" },
  { value: "checkbox", label: "Checkbox (Multi Choice)" },
  { value: "dropdown", label: "Dropdown" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
];

const DATA_SOURCE_CHOICES = [
  { value: "employees", label: "Employee list" },
  { value: "projects", label: "Project" },
  { value: "assets", label: "Assets" },
];

const isOptionType = (type) => ["dropdown", "checkbox", "radio"].includes(type);

const buildDefaultField = (index = 0) => ({
  id: `field-${Date.now()}-${index}`,
  label: "",
  type: "text",
  required: false,
  placeholder: "",
  optionMode: "manual",
  dataSource: "",
  options: ["Option 1", "Option 2"],
});

function SortableQuestionCard({ mode, fieldId, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: fieldId,
  });

  return (
    <section
      ref={setNodeRef}
      style={{
        ...questionCardStyle(mode),
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.75 : 1,
        zIndex: isDragging ? 2 : 1,
      }}
    >
      {children({ attributes, listeners, isDragging })}
    </section>
  );
}

function UseTemplateBuilder() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const template = location.state?.template || null;
  const isEditMode = Boolean(location.state?.isEdit && template?.client_form_id);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 6 },
    }),
  );

  const [formTitle, setFormTitle] = useState(
    template?.title || "Untitled form",
  );
  const [formDescription, setFormDescription] = useState(
    template?.description || "",
  );
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessageType, setSaveMessageType] = useState("success");
  const [isSaving, setIsSaving] = useState(false);
  const [fields, setFields] = useState(() => {
    const source = Array.isArray(template?.schema_json)
      ? template.schema_json
      : [];
    return source.map((field, idx) => ({
      id: field.id || `field-${Date.now()}-${idx}`,
      label: field.label || "",
      type: field.type || "text",
      required: Boolean(field.required),
      placeholder: field.placeholder || "",
      optionMode: field.dataSource ? "dataSource" : "manual",
      dataSource: field.dataSource || "",
      options:
        Array.isArray(field.options) && field.options.length
          ? field.options
          : isOptionType(field.type)
            ? ["Option 1", "Option 2"]
            : [],
    }));
  });

  const canRenderBuilder = useMemo(
    () => template && Array.isArray(template.schema_json),
    [template],
  );

  const updateField = (fieldId, updates) => {
    setFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field,
      ),
    );
  };

  const handleTypeChange = (field, nextType) => {
    if (!isOptionType(nextType)) {
      updateField(field.id, {
        type: nextType,
        optionMode: "manual",
        dataSource: "",
        options: [],
      });
      return;
    }

    updateField(field.id, {
      type: nextType,
      optionMode: field.optionMode || "manual",
      dataSource:
        field.optionMode === "dataSource"
          ? field.dataSource || "employees"
          : "",
      options: field.options?.length ? field.options : ["Option 1", "Option 2"],
    });
  };

  const addField = () =>
    setFields((prev) => [...prev, buildDefaultField(prev.length)]);
  const removeField = (fieldId) =>
    setFields((prev) => prev.filter((field) => field.id !== fieldId));
  const handleSaveTemplateForm = async () => {
    const trimmedTitle = formTitle.trim();
    if (!trimmedTitle) {
      setSaveMessageType("error");
      setSaveMessage("Form title is required.");
      return;
    }
    if (!fields.length) {
      setSaveMessageType("error");
      setSaveMessage("Please add at least one question.");
      return;
    }

    const templateId = template?.id;
    if (!templateId) {
      setSaveMessageType("error");
      setSaveMessage("Template id is missing. Please reopen this form from template library.");
      return;
    }

    const schemaJson = fields.map((field) => ({
      id: field.id,
      label: (field.label || "").trim(),
      type: field.type,
      required: field.required,
      placeholder: field.placeholder || "",
      ...(field.optionMode === "dataSource"
        ? { dataSource: field.dataSource || "employees", options: [] }
        : { options: (field.options || []).map((option) => option.trim()).filter(Boolean) }),
    }));

    const payload = {
      template_id: templateId,
      title: trimmedTitle,
      description: formDescription.trim(),
      schema_json: schemaJson,
      status: "publish",
      is_active: true,
    };

    try {
      setIsSaving(true);
      setSaveMessage("");
      if (isEditMode) {
        await dataAPI.updateDigitalFormClientForm(template.client_form_id, payload);
      } else {
        await dataAPI.createDigitalFormClientForm(payload);
      }
      setSaveMessageType("success");
      setSaveMessage(
        isEditMode
          ? "Client form updated successfully."
          : "Client form created successfully.",
      );
    } catch (error) {
      setSaveMessageType("error");
      setSaveMessage(
        error?.message ||
          (isEditMode ? "Failed to update form." : "Failed to create form."),
      );
    } finally {
      setIsSaving(false);
    }
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setFields((prev) => {
      const oldIndex = prev.findIndex((field) => field.id === active.id);
      const newIndex = prev.findIndex((field) => field.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addOption = (fieldId) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field;
        const nextIndex = (field.options?.length || 0) + 1;
        return {
          ...field,
          options: [...(field.options || []), `Option ${nextIndex}`],
        };
      }),
    );
  };

  const updateOption = (fieldId, optionIndex, value) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field;
        const nextOptions = [...(field.options || [])];
        nextOptions[optionIndex] = value;
        return { ...field, options: nextOptions };
      }),
    );
  };

  const removeOption = (fieldId, optionIndex) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field;
        const nextOptions = (field.options || []).filter(
          (_, idx) => idx !== optionIndex,
        );
        return {
          ...field,
          options: nextOptions.length ? nextOptions : ["Option 1"],
        };
      }),
    );
  };

  if (!canRenderBuilder) {
    return (
      <div style={{ padding: "24px 28px" }}>
        <div style={emptyStateStyle(mode)}>
          No template selected. Please go back and choose a template first.
        </div>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.DIGITAL_FORM_MANAGEMENT)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to library
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div style={layoutStyle()}>
        <aside style={instructionPanelStyle(mode)}>
          <h3 style={instructionTitleStyle(mode)}>Form Builder Help</h3>
          <p style={instructionTextStyle(mode)}>
            Quick guide to build clear, accurate forms: use a clear title and short description, keep one focused question per card, choose the right response type for expected input, use Manual options or Data source for choice fields, and mark required only when truly mandatory.
          </p>

          <div style={instructionSectionTitleStyle(mode)}>Response Type Use Cases</div>
          <ul style={instructionListStyle(mode)}>
            <li><strong>Short Answer</strong>: Names, IDs, short comments, serial numbers.</li>
            <li><strong>Paragraph</strong>: Detailed notes, incident description, handover remarks.</li>
            <li><strong>Option Button (Single Choice)</strong>: One valid choice only (Pass/Fail, Yes/No).</li>
            <li><strong>Checkbox (Multi Choice)</strong>: Multiple selections allowed (PPE items, completed checks).</li>
            <li><strong>Dropdown</strong>: Long option list where one value is selected (site, category, department).</li>
            <li><strong>Date</strong>: Inspection date, due date, service date, expiry date.</li>
            <li><strong>Number</strong>: Meter reading, quantity, temperature, score, runtime hours.</li>
            <li><strong>Email</strong>: Contact email for notifier, approver, or assignee.</li>
          </ul>

          <div style={instructionSectionTitleStyle(mode)}>Choice Field Setup</div>
          <ul style={instructionListStyle(mode)}>
            <li><strong>Manual options</strong>: Use for fixed values managed by form owner.</li>
            <li><strong>Data source</strong>: Use when options should auto-load from live records.</li>
          </ul>
          <div style={instructionNoteStyle(mode)}>
            Data source options available: Employee list, Project, Assets. When Data source is selected, static manual options are not required.
          </div>
        </aside>

        <main style={builderColumnStyle()}>
          <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate(APP_URLS.DIGITAL_FORM_MANAGEMENT)}
              style={backButtonStyle(mode)}
            >
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back to library
            </button>
            <button
              type="button"
              onClick={handleSaveTemplateForm}
              style={saveButtonStyle(isSaving)}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : isEditMode ? "Update client form" : "Save template form"}
            </button>
          </div>

          {saveMessage && (
            <div style={saveMessageStyle(mode, saveMessageType)}>{saveMessage}</div>
          )}

          <section style={formHeaderCardStyle(mode)}>
        <div style={{ display: "grid", gap: 6 }}>
          <label style={headerLabelStyle(mode)}>Form title</label>
          <input
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            style={titleInputStyle(mode)}
            placeholder="Form title"
          />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <label style={headerLabelStyle(mode)}>Description</label>
          <textarea
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            style={descInputStyle(mode)}
            placeholder="Form description"
            rows={3}
          />
        </div>
          </section>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={fields.map((field) => field.id)}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ display: "grid", gap: 12 }}>
            {fields.map((field, fieldIndex) => (
              <SortableQuestionCard
                key={field.id}
                mode={mode}
                fieldId={field.id}
              >
                {({ attributes, listeners }) => (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: mode === "dark" ? "#e4e7ec" : "#1d2939",
                            marginBottom: 2,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            {...attributes}
                            {...listeners}
                            style={dragHandleStyle(mode)}
                            title="Press and drag to reorder"
                          >
                            <DragIndicatorIcon style={{ fontSize: 16 }} />
                          </span>
                          Question {fieldIndex + 1}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: mode === "dark" ? "#98a2b3" : "#667085",
                          }}
                        >
                          Press and drag handle to reorder
                        </div>
                      </div>
                      <div style={{ display: "inline-flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => removeField(field.id)}
                          style={deleteTextBtnStyle()}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 240px 180px",
                        gap: 12,
                      }}
                    >
                      <div style={{ display: "grid", gap: 6 }}>
                        <label style={headerLabelStyle(mode)}>Question</label>
                        <input
                          value={field.label}
                          onChange={(e) =>
                            updateField(field.id, { label: e.target.value })
                          }
                          style={questionInputStyle(mode)}
                          placeholder={`Question ${fieldIndex + 1}`}
                        />
                      </div>
                      <div style={{ display: "grid", gap: 6 }}>
                        <label style={headerLabelStyle(mode)}>
                          Response type
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) =>
                            handleTypeChange(field, e.target.value)
                          }
                          style={selectStyle(mode)}
                        >
                          {FIELD_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: 40,
                          marginTop: 24,
                        }}
                      >
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            color: mode === "dark" ? "#d0d5dd" : "#344054",
                            fontSize: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) =>
                              updateField(field.id, {
                                required: e.target.checked,
                              })
                            }
                          />
                          Mark as required
                        </label>
                      </div>
                    </div>

                    {!isOptionType(field.type) && (
                      <input
                        value={field.placeholder}
                        onChange={(e) =>
                          updateField(field.id, { placeholder: e.target.value })
                        }
                        style={inputStyle(mode)}
                        placeholder="Placeholder"
                      />
                    )}

                    {isOptionType(field.type) && (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div
                          style={{
                            display: "inline-flex",
                            border: `1px solid ${mode === "dark" ? "#3a3a3a" : "#d0d5dd"}`,
                            borderRadius: 10,
                            overflow: "hidden",
                            width: "fit-content",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              updateField(field.id, {
                                optionMode: "manual",
                                dataSource: "",
                              })
                            }
                            style={modeToggleButtonStyle(
                              mode,
                              field.optionMode === "manual",
                            )}
                          >
                            Manual options
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              updateField(field.id, {
                                optionMode: "dataSource",
                                dataSource: field.dataSource || "employees",
                              })
                            }
                            style={modeToggleButtonStyle(
                              mode,
                              field.optionMode === "dataSource",
                            )}
                          >
                            Data source
                          </button>
                        </div>

                        {field.optionMode === "manual" ? (
                          <div style={{ display: "grid", gap: 8 }}>
                            {(field.options || []).map(
                              (option, optionIndex) => (
                                <div
                                  key={`${field.id}-option-${optionIndex}`}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 38px",
                                    gap: 8,
                                    alignItems: "center",
                                  }}
                                >
                                  <input
                                    value={option}
                                    onChange={(e) =>
                                      updateOption(
                                        field.id,
                                        optionIndex,
                                        e.target.value,
                                      )
                                    }
                                    style={inputStyle(mode)}
                                    placeholder={`Option ${optionIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeOption(field.id, optionIndex)
                                    }
                                    style={smallIconBtnStyle(mode)}
                                    title="Remove option"
                                  >
                                    <RemoveCircleOutlineIcon
                                      style={{ fontSize: 18 }}
                                    />
                                  </button>
                                </div>
                              ),
                            )}
                            <button
                              type="button"
                              onClick={() => addOption(field.id)}
                              style={addOptionBtnStyle(mode)}
                            >
                              <AddIcon style={{ fontSize: 16 }} />
                              Add option
                            </button>
                          </div>
                        ) : (
                          <select
                            value={field.dataSource || "employees"}
                            onChange={(e) =>
                              updateField(field.id, {
                                dataSource: e.target.value,
                                options: [],
                              })
                            }
                            style={selectStyle(mode)}
                          >
                            {DATA_SOURCE_CHOICES.map((source) => (
                              <option key={source.value} value={source.value}>
                                {source.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </>
                )}
              </SortableQuestionCard>
            ))}
              </div>
            </SortableContext>
          </DndContext>

          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={addField} style={addFieldButtonStyle()}>
              <AddIcon style={{ fontSize: 18 }} />
              Add question
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

const emptyStateStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#3e3e3e" : "#e3e6e9"}`,
  borderRadius: 10,
  padding: 16,
  color: mode === "dark" ? "#d5d5d5" : "#555",
  background: mode === "dark" ? "#1a1a1a" : "#fff",
  marginBottom: 12,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const formHeaderCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderTop: "8px solid #009391",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  marginBottom: 14,
  display: "grid",
  gap: 12,
});

const questionCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 14,
  display: "grid",
  gap: 12,
});

const dragHandleStyle = (mode) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: 6,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  cursor: "grab",
  userSelect: "none",
});

const titleInputStyle = (mode) => ({
  width: "100%",
  fontSize: 18,
  fontWeight: 600,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  outline: "none",
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "10px 12px",
});

const descInputStyle = (mode) => ({
  width: "100%",
  fontSize: 14,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  outline: "none",
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#bdbdbd" : "#667085",
  padding: "10px 12px",
  resize: "vertical",
});

const headerLabelStyle = (mode) => ({
  fontSize: 13,
  fontWeight: 600,
  color: mode === "dark" ? "#e4e7ec" : "#344054",
});

const questionInputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "10px 12px",
  fontSize: 14,
  fontWeight: 500,
});

const inputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const selectStyle = (mode) => ({
  ...inputStyle(mode),
  padding: "9px 10px",
});

const modeToggleButtonStyle = (mode, active) => ({
  border: "none",
  background: active ? "#009391" : mode === "dark" ? "#191919" : "#fff",
  color: active ? "#fff" : mode === "dark" ? "#d0d5dd" : "#344054",
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
  fontWeight: 500,
});

const smallIconBtnStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#1d1d1d" : "#fff",
  color: mode === "dark" ? "#fda29b" : "#b42318",
  width: 36,
  height: 36,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const deleteTextBtnStyle = () => ({
  border: "none",
  borderRadius: 6,
  background: "#f04438",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  padding: "5px 10px",
  cursor: "pointer",
});

const addOptionBtnStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: "transparent",
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  fontWeight: 500,
  fontSize: 13,
  padding: "8px 10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  width: "fit-content",
});

const addFieldButtonStyle = () => ({
  border: "none",
  borderRadius: 8,
  background: "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const layoutStyle = () => ({
  display: "grid",
  gridTemplateColumns: "65% 35%",
  gap: 18,
  alignItems: "start",
});

const builderColumnStyle = () => ({
  maxWidth: 920,
  order: 1,
});

const instructionPanelStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  position: "sticky",
  top: 76,
  order: 2,
});

const instructionTitleStyle = (mode) => ({
  margin: 0,
  marginBottom: 10,
  fontSize: 16,
  fontWeight: 700,
  color: mode === "dark" ? "#fff" : "#1d2939",
});

const instructionTextStyle = (mode) => ({
  margin: 0,
  marginBottom: 10,
  fontSize: 13,
  color: mode === "dark" ? "#bdbdbd" : "#667085",
  lineHeight: 1.5,
});

const instructionListStyle = (mode) => ({
  margin: 0,
  paddingLeft: 18,
  display: "grid",
  gap: 6,
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  fontSize: 13,
  lineHeight: 1.5,
});

const instructionSectionTitleStyle = (mode) => ({
  marginTop: 12,
  marginBottom: 8,
  fontSize: 13,
  fontWeight: 700,
  color: mode === "dark" ? "#e4e7ec" : "#1d2939",
});

const instructionNoteStyle = (mode) => ({
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px dashed ${mode === "dark" ? "#4a4a4a" : "#cfd4dc"}`,
  color: mode === "dark" ? "#98a2b3" : "#667085",
  fontSize: 12,
});

const saveButtonStyle = (disabled = false) => ({
  border: "none",
  borderRadius: 8,
  background: disabled ? "#73b9b8" : "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "9px 14px",
  cursor: disabled ? "not-allowed" : "pointer",
});

const saveMessageStyle = (mode, type) => ({
  marginBottom: 12,
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
  border:
    type === "error"
      ? `1px solid ${mode === "dark" ? "#7a3d3d" : "#f1b2b2"}`
      : `1px solid ${mode === "dark" ? "#2e6b69" : "#9be3df"}`,
  background:
    type === "error"
      ? mode === "dark"
        ? "#2b1717"
        : "#fff3f3"
      : mode === "dark"
        ? "#122c2b"
        : "#ecfbfa",
  color:
    type === "error"
      ? mode === "dark"
        ? "#ffb0b0"
        : "#a53030"
      : mode === "dark"
        ? "#8de0db"
        : "#0c6a65",
});

export default UseTemplateBuilder;
